import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import type { User } from 'oidc-client-ts';
import { getUserManager } from '@/auth/userManager';
import { callbackTarget } from '@/auth/signInModel';
import { Icon } from '@/components/Icon';
import { AppMark } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

// The code exchange must run once even under StrictMode's double effect, so the promise lives outside React.
let exchange: Promise<User | void> | null = null;

/** Runs the code exchange, lands on the stored target and returns the failure message (null while pending/ok). */
function useCodeExchange(): string | null {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    exchange ??= getUserManager().signinCallback();
    exchange
      .then((user) => {
        void navigate({ to: callbackTarget(user) as '/', replace: true });
      })
      .catch((e: unknown) => {
        exchange = null;
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [navigate]);

  return error;
}

function CallbackFailed({ message }: { message: string }) {
  return (
    <div role="alert">
      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-destructive/30 bg-destructive/10 text-destructive">
          <Icon name="error" size={22} />
        </span>
        <div>
          <h1 className="text-base font-semibold text-foreground">Sign-in could not be completed</h1>
          <p className="mt-1 break-words text-[13px] text-muted-foreground">{message}</p>
        </div>
      </div>
      <Button asChild variant="outline" className="mt-5 w-full">
        <Link to="/sign-in" search={{ redirect: undefined, reason: undefined }}>
          Back to sign-in
        </Link>
      </Button>
    </div>
  );
}

function CallbackPending() {
  return (
    <div className="flex items-center gap-3">
      <AppMark size={36} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-foreground">Completing sign-in…</div>
        <div className="text-xs text-muted-foreground">Exchanging the authorization code</div>
        <Skeleton className="mt-3 h-1.5 w-full" />
      </div>
    </div>
  );
}

export function AuthCallback() {
  const error = useCodeExchange();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <Card className="fade-in w-full max-w-[400px] rounded-xl p-6 shadow-overlay" aria-live="polite">
        {error ? <CallbackFailed message={error} /> : <CallbackPending />}
      </Card>
    </div>
  );
}
